<script setup lang="ts">
import {
  RECURRING_END_CONDITION_TYPES,
  RECURRING_FREQUENCIES,
  type RecurringEndConditionType,
  type RecurringFrequency,
} from '@/types/recurringDonation'
import DashboardFormField from '@/components/dashboard/ui/DashboardFormField.vue'
import DashboardDateInput from '@/components/dashboard/ui/DashboardDateInput.vue'

const frequency = defineModel<RecurringFrequency>('frequency', {
  required: true,
})
const endType = defineModel<RecurringEndConditionType>('endType', {
  required: true,
})
const endDate = defineModel<string>('endDate', { required: true })
const occurrenceCount = defineModel<string>('occurrenceCount', {
  required: true,
})

withDefaults(
  defineProps<{
    idPrefix?: string
    showNextDate?: boolean
    nextDate?: string
    nextDateError?: string
    frequencyError?: string
    endDateError?: string
    occurrenceCountError?: string
    minNextDate?: string
  }>(),
  {
    idPrefix: 'recurring',
    showNextDate: false,
    nextDate: '',
    nextDateError: '',
    frequencyError: '',
    endDateError: '',
    occurrenceCountError: '',
    minNextDate: undefined,
  }
)

const emit = defineEmits<{
  'update:nextDate': [value: string]
}>()

// Option order comes from the shared constants so the select, the client
// guards and the mock validator can never disagree.
const frequencies = RECURRING_FREQUENCIES

const frequencyLabelKey: Record<RecurringFrequency, string> = {
  weekly: 'Dashboard.Giving.AddDonationModal.FrequencyWeekly',
  monthly: 'Dashboard.Giving.AddDonationModal.FrequencyMonthly',
  quarterly: 'Dashboard.Giving.AddDonationModal.FrequencyQuarterly',
  annually: 'Dashboard.Giving.AddDonationModal.FrequencyAnnually',
}

const endTypes = RECURRING_END_CONDITION_TYPES

const endLabelKey: Record<RecurringEndConditionType, string> = {
  until_stopped: 'Dashboard.Giving.AddDonationModal.EndUntilStopped',
  on_date: 'Dashboard.Giving.AddDonationModal.EndOnDate',
  after_occurrences: 'Dashboard.Giving.AddDonationModal.EndAfterOccurrences',
}
</script>

<template>
  <DashboardFormField
    :label="$t('Dashboard.Giving.AddDonationModal.RepeatsLabel')"
    :label-for="`${idPrefix}-frequency`"
    :error="frequencyError"
  >
    <template #default="{ errorId, hasError }">
      <select
        :id="`${idPrefix}-frequency`"
        v-model="frequency"
        class="sa-input sa-input--select"
        :aria-invalid="hasError || undefined"
        :aria-describedby="hasError ? errorId : undefined"
      >
        <option v-for="option in frequencies" :key="option" :value="option">
          {{ $t(frequencyLabelKey[option]) }}
        </option>
      </select>
    </template>
  </DashboardFormField>

  <DashboardFormField
    v-if="showNextDate"
    :label="$t('Dashboard.RecurringDonationModal.NextDateLabel')"
    :label-for="`${idPrefix}-next-date`"
    :error="nextDateError"
  >
    <template #default="{ errorId, hasError }">
      <DashboardDateInput
        :id="`${idPrefix}-next-date`"
        :model-value="nextDate"
        :min="minNextDate"
        :aria-invalid="hasError || undefined"
        :aria-describedby="hasError ? errorId : undefined"
        @update:model-value="emit('update:nextDate', $event)"
      />
    </template>
  </DashboardFormField>

  <DashboardFormField
    :label="$t('Dashboard.Giving.AddDonationModal.EndsLabel')"
    :label-for="`${idPrefix}-end-type`"
  >
    <template #default>
      <select
        :id="`${idPrefix}-end-type`"
        v-model="endType"
        class="sa-input sa-input--select"
      >
        <option v-for="option in endTypes" :key="option" :value="option">
          {{ $t(endLabelKey[option]) }}
        </option>
      </select>
    </template>
  </DashboardFormField>

  <DashboardFormField
    v-if="endType === 'on_date'"
    :label="$t('Dashboard.Giving.AddDonationModal.EndDateLabel')"
    :label-for="`${idPrefix}-end-date`"
    :error="endDateError"
  >
    <template #default="{ errorId, hasError }">
      <DashboardDateInput
        :id="`${idPrefix}-end-date`"
        v-model="endDate"
        :aria-invalid="hasError || undefined"
        :aria-describedby="hasError ? errorId : undefined"
      />
    </template>
  </DashboardFormField>

  <DashboardFormField
    v-if="endType === 'after_occurrences'"
    :label="$t('Dashboard.Giving.AddDonationModal.OccurrenceCountLabel')"
    :label-for="`${idPrefix}-occurrence-count`"
    :error="occurrenceCountError"
  >
    <template #default="{ errorId, hasError }">
      <input
        :id="`${idPrefix}-occurrence-count`"
        v-model="occurrenceCount"
        class="sa-input"
        type="number"
        inputmode="numeric"
        min="1"
        step="1"
        :aria-invalid="hasError || undefined"
        :aria-describedby="hasError ? errorId : undefined"
      />
    </template>
  </DashboardFormField>
</template>
